/**
 * Store Config Hook using TanStack Query
 * Fetches store configuration with automatic caching
 */
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { StoreConfigResource } from '../../core/resources/storeConfig';
import { useTagadaContext } from '../providers/TagadaProvider';
import { getGlobalApiClient } from './useApiQuery';
import { usePluginConfig } from './usePluginConfig';
export function useStoreConfigQuery(options = {}) {
    const { storeId: providedStoreId, enabled = true } = options;
    const { storeId: contextStoreId } = usePluginConfig();
    const { isSessionInitialized } = useTagadaContext();
    // Use provided storeId or fall back to context storeId
    const storeId = providedStoreId || contextStoreId;
    // Create store config resource client
    const storeConfigResource = useMemo(() => {
        try {
            return new StoreConfigResource(getGlobalApiClient());
        }
        catch (error) {
            throw new Error('Failed to initialize store config resource: ' + (error instanceof Error ? error.message : 'Unknown error'));
        }
    }, []);
    // Use TanStack Query for fetching store config
    const { data: storeConfig, isLoading, error, isSuccess, refetch } = useQuery({
        queryKey: ['storeConfig', storeId],
        enabled: enabled && !!storeId && isSessionInitialized,
        queryFn: async () => {
            if (!storeId) {
                throw new Error('Store ID not found. Make sure the TagadaProvider is properly configured.');
            }
            return await storeConfigResource.getStoreConfig(storeId);
        },
        staleTime: 5 * 60 * 1000, // 5 minutes - config doesn't change often
        gcTime: 10 * 60 * 1000, // 10 minutes cache time
    });
    // Refetch function
    const refetchConfig = useCallback(async () => {
        await refetch();
    }, [refetch]);
    return {
        // Query data
        storeConfig,
        isLoading,
        error,
        isSuccess,
        // Actions
        refetch: refetchConfig,
    };
}
